import React from "react";
import { Helmet } from "react-helmet-async";
import { useCompany } from "../../hooks/useData";
import { useContactInfo } from "../../hooks/useData";
import { useSoftware } from "../../hooks/useData";
import { useLocations } from "../../hooks/useData";

const StructuredData = () => {
  const company = useCompany();
  const contact = useContactInfo();
  const softwareData = useSoftware();
  const locationsData = useLocations();
  const software = softwareData.software;
  const branches = locationsData.locations.branches;

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: company.name,
    legalName: company.legalName,
    url: "https://www.muskansoftwares.com",
    logo: "https://www.muskansoftwares.com/muskan-logo.png",
    description: company.description,
    foundingDate: company.founded,
    address: {
      "@type": "PostalAddress",
      addressCountry: "PK",
      addressLocality: "Pakistan",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: `+${contact.phone}`,
      contactType: "Customer Service",
      areaServed: "PK",
      availableLanguage: ["en", "ur"],
    },
    sameAs: ["https://www.facebook.com/muskancomputerservices"],
  };

  // LocalBusiness Schemas for each branch
  const localBusinessSchemas = branches.map((branch) => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `${company.name} - ${branch.city}`,
    description: `${company.description} - ${branch.type}`,
    address: {
      "@type": "PostalAddress",
      addressLocality: branch.city,
      addressRegion: branch.province,
      addressCountry: "PK",
      streetAddress: branch.address,
    },
    telephone: `+${branch.phone}`,
    priceRange: "$$",
    areaServed: {
      "@type": "Country",
      name: "Pakistan",
    },
  }));

  // Software Applications Schema
  const softwareSchemas = software
    .map((product) => ({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: product.name,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows",
      description: product.description,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "PKR",
        availability: "https://schema.org/InStock",
      },
      aggregateRating:
        product.status === "live"
          ? {
              "@type": "AggregateRating",
              ratingValue: "5",
              ratingCount: "50",
            }
          : undefined,
    }))
    .filter((schema) => schema !== null);

  // Tools Schema (WebApplication)
  const tools = [
    {
      name: "Free Invoice Generator Pakistan",
      url: "https://www.muskansoftwares.com/tools/invoice-generator",
      description:
        "Create professional PDF invoices online for free. No login needed.",
      applicationCategory: "FinanceApplication",
    },
    {
      name: "Commission Calculator",
      url: "https://www.muskansoftwares.com/tools/commission-calculator",
      description:
        "Calculate commission, expenses, and net payable amount for Mandi agents.",
      applicationCategory: "FinanceApplication",
    },
    {
      name: "Business Health Checkup",
      url: "https://www.muskansoftwares.com/tools/health-checkup",
      description:
        "Audit your business performance and health score in 2 minutes.",
      applicationCategory: "BusinessApplication",
    },
    {
      name: "AI Audio Transcriber",
      url: "https://www.muskansoftwares.com/tools/ai-transcriber",
      description:
        "Convert WhatsApp audio notes and recordings to text instantly using AI.",
      applicationCategory: "UtilitiesApplication",
    },
    {
      name: "Build vs Buy ROI Calculator",
      url: "https://www.muskansoftwares.com/tools/build-vs-buy-calculator",
      description:
        "Calculate the ROI of outsourcing software development vs building an in-house team.",
      applicationCategory: "FinanceApplication",
    },
    {
      name: "SaaS Revenue Visualizer",
      url: "https://www.muskansoftwares.com/tools/saas-pricing-visualizer",
      description:
        "Visualize your SaaS startup revenue projections and MRR/ARR growth.",
      applicationCategory: "FinanceApplication",
    },
    {
      name: "Free Online Typing Tutor",
      url: "https://www.muskansoftwares.com/tools/online-typing-tutor",
      description:
        "Improve your typing speed and accuracy with 15 structured lessons. Professional certifications included.",
      applicationCategory: "EducationApplication",
    },
  ];

  const toolSchemas = tools.map((tool) => ({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: tool.name,
    url: tool.url,
    applicationCategory: tool.applicationCategory,
    operatingSystem: "Any",
    description: tool.description,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "PKR",
    },
    browserRequirements: "Requires JavaScript. Works in all modern browsers.",
  }));

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      {localBusinessSchemas.map((schema, index) => (
        <Helmet key={`local-${index}`}>
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      ))}
      {softwareSchemas.map((schema, index) => (
        <Helmet key={`software-${index}`}>
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      ))}
      {toolSchemas.map((schema, index) => (
        <Helmet key={`tool-${index}`}>
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      ))}
    </>
  );
};

export default StructuredData;
